import React from 'react';
import Button, { ButtonProps } from '@material-ui/core/Button';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import Menu, { MenuProps } from '@material-ui/core/Menu';
import { makeStyles } from '@material-ui/core/styles';
import AddCircleOutline from '@material-ui/icons/AddCircleOutline';
import { useTranslation } from '../../hooks/useTranslation';
import colors from '../../themes/main-colors';
import { PaymentCategory, PaymentProfile } from '../../__generated__/pge-types';
import { EditProps, ProfileInfoChangeHandler } from '../paymentus/types';
import EditPaymentProfile from '../paymentus/edit-payment-profile';
import PaymentCategoryIcon from '../paymentus/payment-profile-icon/PaymentCategoryIcon';
import { Typography } from '@material-ui/core';
import useWrapWithLoader from '../../hooks/useWrapWithLoading';
import { useRefetchListProfiles } from '../../hooks/usePaymentus';
import { useIsApplePayEnabled } from '../../hooks/useIsApplePayEnabled';

const StyledButton = ({ ...rest }: ButtonProps) => (
  <Button
    fullWidth
    aria-haspopup="true"
    variant="outlined"
    endIcon={<AddCircleOutline />}
    style={{
      background: colors.lightBlue,
    }}
    disableRipple={false}
    {...rest}
  />
);

interface StyledMenuProps extends MenuProps {
  width: number;
}

const useStyledMenuStyles = makeStyles(() => ({
  paper: {
    width: (props: { width: number }) => props.width,
  },
}));

const StyledMenu = (props: StyledMenuProps) => {
  const classes = useStyledMenuStyles(props);

  return (
    <Menu
      getContentAnchorEl={null}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'center',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'center',
      }}
      classes={classes}
      {...props}
    />
  );
};

type Props = {
  ownerId?: string;
  onNew?: ProfileInfoChangeHandler;
  onSelectCategory?: (flag: boolean) => void;
  customLabel?: string;
  allowedCategories: PaymentCategory[];
  isPrimaryPM?: boolean;
};

export default function AddPaymentMethodDropdown(props: Props) {
  const { t } = useTranslation();
  const [width, setWidth] = React.useState(0);
  const [open, setOpen] = React.useState(false);
  const anchorEl = React.useRef<null | HTMLElement>(null);
  const {
    ownerId,
    onNew,
    onSelectCategory,
    customLabel,
    allowedCategories,
    isPrimaryPM,
  } = props;

  const [selected, setSelected] = React.useState(undefined);
  const [isShow, setIsShow] = React.useState(false);
  const [editProps, setEditProps] = React.useState<EditProps>();
  const { refetch } = useRefetchListProfiles(ownerId);
  const { wrapWithLoader } = useWrapWithLoader();
  const isApplePayEnabled = useIsApplePayEnabled();

  const handleRefetch = wrapWithLoader(refetch);

  const replaceProfileIfReqd = async (profile: PaymentProfile) => {
    if (
      ownerId &&
      !profile?.cardHolderName &&
      [
        PaymentCategory.PaypalAccount,
        PaymentCategory.PaypalCredit,
        ...(isApplePayEnabled ? [PaymentCategory.Ap] : []),
        PaymentCategory.Venmo,
      ]
        .map(p => p.toString())
        .some(p => p === profile.type)
    ) {
      // force refresh because we do not have card name for
      // these new profile types returned from the iframe
      // in the onNew payload

      const { data } = await handleRefetch();
      const profiles = data?.listProfiles?.listProfilesResponse?.profile;

      return profiles?.find(p => p.token === profile.token) || profile;
    }

    return profile;
  };

  const paymentMethodTypes = allowedCategories?.map(allowedPaymentCategory => {
    switch (allowedPaymentCategory) {
      case PaymentCategory.Dd:
        return {
          label: t('BANK_ACCOUNT'),
          pmCategory: PaymentCategory.Dd,
          icon: 'BANK',
        };
      case PaymentCategory.Cc:
        return {
          label: t('CREDIT_CARD'),
          pmCategory: PaymentCategory.Cc,
          icon: 'CARD',
        };
      case PaymentCategory.Dc:
        return {
          label: t('DEBIT_CARD'),
          pmCategory: PaymentCategory.Dc,
          icon: 'CARD',
        };
      case PaymentCategory.PaypalAccount:
        return {
          label: t('PAYPAL_ACCOUNT'),
          pmCategory: PaymentCategory.PaypalAccount,
          icon: 'PAYPAL_ACCOUNT',
        };
      case PaymentCategory.PaypalCredit:
        return {
          label: t('PAYPAL_CREDIT'),
          pmCategory: PaymentCategory.PaypalCredit,
          icon: 'PAYPAL_CREDIT',
        };
      case PaymentCategory.Ap:
        return isApplePayEnabled
          ? {
              label: t('APPLE_PAY'),
              pmCategory: PaymentCategory.Ap,
              icon: 'APPLE_PAY',
            }
          : undefined;
      case PaymentCategory.Venmo:
        return {
          label: t('VENMO'),
          pmCategory: PaymentCategory.Venmo,
          icon: 'VENMO',
        };
    }
  });

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setWidth(event.currentTarget.clientWidth);
    anchorEl.current = event.currentTarget;
    setOpen(true);
    onSelectCategory && onSelectCategory(true);
  };

  const handleSelectProfile = (pmCategory: PaymentCategory) => {
    setEditProps({
      iframeProps: {
        ownerId: ownerId,
        pmCategory,
        postMessagePmDetailsOrigin: location.origin,
        primaryPM: isPrimaryPM,
      },
      onNew: async (profile: PaymentProfile) => {
        onSelectCategory && onSelectCategory(false);
        setIsShow(false);
        setOpen(false);
        setSelected(undefined);
        onNew &&
          onNew({
            profile: await replaceProfileIfReqd(profile),
            pmCategory,
            isAnonymous: !ownerId,
          });
      },
      onClose: () => {
        setIsShow(false);
        setOpen(false);
        setSelected(undefined);
        onSelectCategory && onSelectCategory(false);
        window.scrollTo(0, 0);
      },
    });
    setIsShow(true);
  };

  const handleSelect = (paymentMethodType: any) => {
    handleSelectProfile(paymentMethodType.pmCategory);
    setIsShow(true);
  };

  return (
    <>
      <StyledButton onClick={handleClick}>
        <Typography style={{ flex: '1 0 auto', textAlign: 'left' }}>
          {selected || customLabel || t('ADD_A_PAYMENT_METHOD')}
        </Typography>
      </StyledButton>
      <StyledMenu
        width={width}
        keepMounted
        open={open}
        onClose={() => {
          setOpen(false);
          onSelectCategory && onSelectCategory(false);
        }}
        anchorEl={anchorEl.current}
      >
        {paymentMethodTypes.map(
          (paymentMethodType, idx) =>
            paymentMethodType && (
              <ListItem
                key={paymentMethodType.label}
                selected={paymentMethodType.label === selected}
                onClick={() => handleSelect(paymentMethodType)}
                style={
                  idx < paymentMethodTypes.length - 1
                    ? {
                        borderBottom: `1px solid ${colors.lightGray1}`,
                      }
                    : {}
                }
              >
                <ListItemIcon>
                  <PaymentCategoryIcon type={paymentMethodType.icon} />
                </ListItemIcon>
                <ListItemText
                  secondary={paymentMethodType.label}
                  style={{ flex: '1 0 auto' }}
                  primaryTypographyProps={{
                    style: {
                      fontWeight: 'bold',
                      color: colors.noirBlur,
                    },
                  }}
                />
              </ListItem>
            ),
        )}
      </StyledMenu>
      {isShow && editProps && (
        <EditPaymentProfile open={isShow} editProps={editProps} />
      )}
    </>
  );
}
